import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDeliveryStatus } from "@/components/delivery/useDeliveryStatus";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User2, KeyRound, Save, LogOut, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { user, logout } = useAuth();
  const { online, toggle } = useDeliveryStatus();
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [stats, setStats] = useState({ delivered: 0, cancelled: 0, total: 0 });

  useEffect(() => {
    api.get("/orders/assigned-to-me?limit=200")
      .then((r) => {
        const items = r.data.items || [];
        setStats({
          delivered: items.filter((o) => o.status === "delivered").length,
          cancelled: items.filter((o) => ["cancelled", "returned"].includes(o.status)).length,
          total: items.length,
        });
      }).catch(() => {});
  }, []);

  const changePw = async () => {
    if (!pw.current || pw.next.length < 8) { toast.error("New password must be 8+ chars"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated"); setPw({ current: "", next: "" });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPwBusy(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="delivery-profile-page">
      <Card className="card-soft border-transparent overflow-hidden">
        <div className="bg-gradient-to-br from-accent to-[hsl(var(--brand-red-deep))] p-5 text-accent-foreground">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur grid place-items-center text-xl font-semibold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-xl font-display font-semibold truncate">{user?.name || "Delivery partner"}</div>
              <div className="text-sm opacity-85 truncate">{user?.email}</div>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-white/15 hover:bg-white/25 text-accent-foreground border-transparent">{user?.role}</Badge>
                <span className={`text-xs flex items-center gap-1 ${online ? "" : "opacity-70"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-white" : "bg-white/40"}`} />
                  {online ? "Online" : "Offline"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><div className="text-2xl font-display font-semibold">{stats.delivered}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Delivered</div></div>
            <div><div className="text-2xl font-display font-semibold text-destructive">{stats.cancelled}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Cancelled</div></div>
            <div><div className="text-2xl font-display font-semibold">{stats.total}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total runs</div></div>
          </div>
        </CardContent>
      </Card>

      {/* Availability */}
      <Card className="card-soft border-transparent" data-testid="profile-availability-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent grid place-items-center"><User2 className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-medium">Availability</div>
            <div className="text-xs text-muted-foreground">Toggle off when you're on a break</div>
          </div>
          <Switch checked={online} onCheckedChange={(v) => toggle(v)} className="data-[state=checked]:bg-accent" data-testid="profile-online-toggle" />
        </CardContent>
      </Card>

      {/* Account */}
      <Card className="card-soft border-transparent">
        <CardContent className="p-4 text-sm space-y-1">
          <div className="font-display text-base mb-2">Account details</div>
          <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span>{user?.name || "—"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span>{user?.phone || "—"}</span></div>
          {user?.created_at && <div className="flex justify-between"><span className="text-muted-foreground">Joined</span><span>{fmtDate(user.created_at)}</span></div>}
        </CardContent>
      </Card>

      {/* Password */}
      <Card className="card-soft border-transparent" data-testid="profile-password-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4 text-accent" /><div className="font-display text-base">Change password</div></div>
          <div className="space-y-2">
            <div><Label>Current password</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="del-pw-current" /></div>
            <div><Label>New password</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="8+ chars, letters + digits" data-testid="del-pw-next" /></div>
            <Button onClick={changePw} disabled={pwBusy} className="btn-primary-glow w-full" data-testid="del-save-pw"><Save className="w-4 h-4 mr-1" />{pwBusy ? "Saving…" : "Update password"}</Button>
          </div>
        </CardContent>
      </Card>

      <div className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1">
        <ShieldCheck className="w-3 h-3" /> Only your assigned orders are visible to you.
      </div>

      <Button variant="outline" onClick={logout} className="w-full text-destructive hover:text-destructive hover:bg-destructive/5 border-destructive/20" data-testid="del-signout">
        <LogOut className="w-4 h-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}
